
import { supabase } from "@/integrations/supabase/client";
import type { Signature } from "@/types/supabase";
import type { User } from "@supabase/supabase-js";

export class SignatureService {
  constructor(private user: User | null) {}

  private async validateSession() {
    const { data: { session }, error } = await supabase.auth.getSession();
    if (error || !session) {
      throw new Error('No active session');
    }
    return session;
  }

  async saveSignature(text: string, svgContent: string): Promise<Signature> {
    if (!this.user) {
      throw new Error('User not authenticated');
    }

    try {
      await this.validateSession();
      
      const { data, error } = await supabase
        .from('signatures')
        .insert([
          {
            user_id: this.user.id,
            text,
            svg_content: svgContent,
          },
        ])
        .select()
        .single();

      if (error) throw new Error(`Failed to save signature: ${error.message}`);
      if (!data) throw new Error('Failed to save signature: No data returned');

      return data;
    } catch (err) {
      throw new Error(`Failed to save signature: ${err instanceof Error ? err.message : 'Network error'}`);
    }
  }

  async deleteSignature(signatureId: string): Promise<void> {
    if (!this.user) {
      throw new Error('User not authenticated');
    }

    try {
      await this.validateSession();
      
      const { error } = await supabase
        .from('signatures')
        .delete()
        .eq('id', signatureId)
        .eq('user_id', this.user.id);

      if (error) throw new Error(`Failed to delete signature: ${error.message}`);
    } catch (err) {
      throw new Error(`Failed to delete signature: ${err instanceof Error ? err.message : 'Network error'}`);
    }
  }
}
